import {
  againField,
  questionField,
  spokenField,
  typedField,
  versionField,
} from "../content/lianxi/exercise-fields.js";
import { afterFinish, joined, offerAfterFinish } from "./lianxi-finish-copy.js";
import type { HandoverCount } from "./lianxi-handover.js";
import { answerHandoverCount } from "./lianxi-handover.js";
import type { ExercisePress, RecordAnswer } from "./lianxi-post.js";
import type { HearVerdict } from "./lianxi-verdict-sounds.js";
import { shuociCopy } from "./lianxi-shuoci-copy.js";
import { wholeNumbers } from "./lianxi-yuedu.js";
import {
  type Said,
  type SpeechAudio,
  speechSaid,
  tellOn,
} from "./hold-to-speak.js";

/**
 * A 语音答题 Exercise on its page: each word asked by its English, said aloud or
 * typed, and a word missed asked again later in the sitting (CB-85).
 *
 * `partials/lianxi/shuoci.html` draws every word as an item, hidden, with its
 * English and, hidden, the word and its reading, and `./lianxi-shuoci-start.ts`
 * builds this over them. The question is the one an `english_to_speech` card
 * asks in 复习, with the controls drawn the same way, and the button is
 * `./hold-to-speak.ts`, the card's own.
 *
 * **A signed-in learner's answers are marked by the handler**, spoken or
 * typed, and the page shows what it says. A spoken answer cannot be marked
 * anywhere else, and a typed one is marked there too so the two never
 * disagree. The page waits for each answer, and the button and the field are
 * held while it does, so the answers reach the handler one at a time.
 *
 * **A try that sounds like another word earns one more**, with the reading it
 * sounded like and the word played, as on the card. A second is a miss. A
 * try with nothing usable in it is "didn't catch that" and costs nothing. A
 * first try heard as another word that means the same is named, and the
 * learner tries a different word, as on the card.
 * Running out of spoken attempts, or the hearing failing, opens the keyboard,
 * and the Exercise goes on. A learner without Pro who runs out is offered Pro,
 * as on the card.
 *
 * **A visitor types.** They have no spoken attempts, so the button is never
 * shown to them and a link offers a sign-in instead. Their typing is marked
 * in the browser against the word and its other writings, which the item
 * carries, and nothing is kept, as on every other Exercise. A signed-in
 * learner whose press the handler cannot answer is marked the same way.
 */

/**
 * A learner's place, as the handler or the page holds it. A word on its one
 * more try is the handler's to know, and the page asks it again as any other.
 */
export interface ShuociPlace {
  readonly queue: readonly number[];
  readonly missed: readonly number[];
}

/** What keeps a signed-in learner's place, and hears what they say. */
export interface ShuociKeeper {
  readonly press: Pick<ExercisePress, "post">;

  /** The version the page was drawn from, which every answer names. */
  readonly version: string;
  handedOver: boolean;
  handoverCount?: HandoverCount | undefined;

  /** Whether the learner is on a free account, who is offered Pro at the finish. */
  readonly free: boolean;
}

/** The controls under the words, which every word is answered with. */
export interface ShuociControls {
  /** The line after a try: what it sounded like, or why it was not heard. */
  readonly note: HTMLElement;

  /** The button held while speaking, where the page has one. */
  readonly speak: HTMLButtonElement | null;

  /** The status the button speaks through. */
  readonly status: HTMLElement | null;

  /** The way to sign in, for a visitor. */
  readonly visitor: HTMLElement | null;

  /** The keyboard, behind its summary. */
  readonly typing: HTMLDetailsElement;
  readonly field: HTMLInputElement;
}

/** What the Exercise is built over. */
export interface ShuociExerciseProperties {
  /** Every word's item, in the order asked. */
  readonly items: readonly HTMLElement[];
  readonly controls: ShuociControls;
  readonly progress: HTMLElement;

  /** The bar under the progress line, filled to the words done. */
  readonly meter?: HTMLElement | null | undefined;
  readonly next: HTMLButtonElement;
  readonly mark: HTMLElement;
  readonly finished: HTMLElement;
  readonly finishedLine: HTMLElement;

  /** Where the handler left the learner, for a signed-in learner. */
  readonly kept?: ShuociPlace | undefined;
  readonly keeper?: ShuociKeeper | undefined;

  /**
   * Whether the word at a place is past a visitor's taste, which shows the
   * gate in its place. Nothing for a learner who plays to the end.
   */
  readonly gate?: ((at: number) => boolean) | undefined;

  /** What plays the note for a verdict. Nothing for a silent Exercise. */
  readonly hear?: HearVerdict | undefined;

  /** What plays the word after a miss. */
  readonly audio: Pick<SpeechAudio, "play">;
}

/** The handler's mark on one word, as far as the page reads it. */
interface Marked {
  readonly question: number;
  readonly verdict: string;
  readonly heard?: string;
  readonly again?: boolean;
  readonly say?: string;
  readonly hint?: { readonly syllable: number; readonly part: string };
  readonly rival?: string;
  readonly means?: string;
  readonly reason?: string;
  readonly pro?: boolean;
  readonly close?: string;
}

/** The mark an answer carries, where it is one the page can read. */
function markedOf(answer: RecordAnswer | undefined): Marked | undefined {
  const marked = answer?.marked;

  return typeof marked === "object" &&
    marked !== null &&
    typeof (marked as Marked).verdict === "string" &&
    Number.isInteger((marked as Marked).question)
    ? (marked as Marked)
    : undefined;
}

/** A 语音答题 Exercise on its page. */
export class ShuociExercise {
  #queue: number[] = [];
  #missed: number[] = [];

  /** Whether the word showing has been marked. */
  #marked = false;

  /** Whether an answer is with the handler, which holds the controls. */
  #waiting = false;

  constructor(private readonly properties: ShuociExerciseProperties) {}

  /** Start at the kept place, or at the first word. */
  start(): void {
    const { kept, items } = this.properties;

    this.#queue = [...(kept?.queue ?? items.map((_, at) => at))];
    this.#missed = [...(kept?.missed ?? [])];
    this.#showHead();
  }

  /** Whether the learner can speak, which a visitor cannot. */
  canSpeak(): boolean {
    return this.properties.keeper !== undefined;
  }

  /**
   * Mark what the learner said, on the handler.
   * @param spoken - The recording, as `./hold-to-speak.ts` gives it.
   */
  async speak(spoken: string): Promise<void> {
    const { keeper, controls } = this.properties;
    const at = this.#queue[0];

    if (keeper === undefined || at === undefined || !this.#answerable()) {
      return;
    }

    tellOn(controls.status, speechSaid.checking);
    this.#hold(true);

    const answer = await this.#post({
      [questionField]: String(at),
      [spokenField]: spoken,
    });

    this.#hold(false);
    tellOn(controls.status, undefined);

    const marked = markedOf(answer);

    if (answer === undefined || marked === undefined) {
      // Nothing came back, or the answer was stale: the place as it stands.
      if (answer === undefined) {
        this.#say(shuociCopy.unavailable);
        this.#offerTyping();
      } else {
        this.#showHead();
      }

      return;
    }

    this.#showMark(marked);
  }

  /** Mark what the learner typed, on the handler or in the page. */
  async type(): Promise<void> {
    const { keeper, controls } = this.properties;
    const at = this.#queue[0];
    const typed = controls.field.value.trim();

    if (at === undefined || typed === "" || !this.#answerable()) {
      return;
    }

    if (keeper !== undefined) {
      this.#hold(true);

      const answer = await this.#post({
        [questionField]: String(at),
        [typedField]: typed,
      });

      this.#hold(false);

      const marked = markedOf(answer);

      if (marked !== undefined) {
        this.#showMark(marked);

        return;
      }

      if (answer !== undefined) {
        this.#showHead();

        return;
      }
    }

    const right = this.#accepted(at).includes(typed);

    this.#moveHere(at, right);
    this.#showMarked({ right });
  }

  /** Go on from a marked word. */
  next(): void {
    if (this.#marked) {
      this.#showHead();
    }
  }

  /** Whether the word showing is marked, which Enter goes on from. */
  isMarked(): boolean {
    return this.#marked;
  }

  /** Start the Exercise again from its first word. */
  again(): void {
    this.#queue = this.properties.items.map((_, at) => at);
    this.#missed = [];
    void this.#post({ [againField]: "" });
    this.#showHead();
  }

  /** Whether the word showing is waiting for an answer. */
  #answerable(): boolean {
    return !this.#marked && !this.#waiting;
  }

  /** What the item at a place accepts as typed, for marking in the page. */
  #accepted(at: number): readonly string[] {
    return (
      this.properties.items[at]?.getAttribute("data-cb-shuoci-accepted") ?? ""
    )
      .split(" ")
      .filter(Boolean);
  }

  /** Move the queue as the handler would, for an answer marked in the page. */
  #moveHere(at: number, right: boolean): void {
    const rest = this.#queue.slice(1);

    this.#queue = right ? rest : [...rest, at];
    this.#missed =
      right || this.#missed.includes(at) ? this.#missed : [...this.#missed, at];
  }

  /** Hold the controls while an answer is with the handler, or let them go. */
  #hold(holding: boolean): void {
    const { speak, field } = this.properties.controls;

    this.#waiting = holding;
    field.disabled = holding;

    if (speak !== null) {
      speak.disabled = holding;
    }
  }

  /** Show what the handler made of an answer. */
  #showMark(marked: Marked): void {
    const { controls, audio } = this.properties;

    switch (marked.verdict) {
      case "right": {
        this.#showMarked({
          right: true,
          ...(marked.close === undefined
            ? {}
            : { heard: shuociCopy.closeTo(marked.close) }),
          ...(marked.hint === undefined
            ? {}
            : {
                hint: shuociCopy.hint(marked.hint.syllable, marked.hint.part),
              }),
        });
        break;
      }

      case "otherwise": {
        const heard = marked.heard ?? "";

        if (marked.again === true) {
          this.#say(shuociCopy.tryAgain(heard), marked.say);
        } else {
          this.#showMarked({
            right: false,
            heard: shuociCopy.soundedLike(heard),
          });
        }

        if (marked.say !== undefined) {
          audio.play(marked.say);
        }

        break;
      }

      case "synonym": {
        this.#say(shuociCopy.synonym(marked.rival ?? "", marked.means ?? ""));
        break;
      }

      case "wrong": {
        this.#showMarked({ right: false });
        break;
      }

      case "unclear": {
        tellOn(controls.status, speechSaid.unclear);
        break;
      }

      case "refused": {
        this.#refused(marked);

        if (controls.speak !== null) {
          controls.speak.hidden = true;
        }

        this.#offerTyping();
        break;
      }

      default: {
        this.#say(shuociCopy.unavailable);
        this.#offerTyping();
      }
    }
  }

  /**
   * Say the day's spoken attempts are used. A learner without Pro is told the
   * free line and offered Pro, and a Pro learner is told the line alone.
   */
  #refused(marked: Marked): void {
    if (marked.pro !== false) {
      this.#say(shuociCopy.refused);

      return;
    }

    const { note } = this.properties.controls;
    const link = note.ownerDocument.createElement("a");

    this.#say(
      shuociCopy.freeRefused[marked.reason === "paused" ? "paused" : "spent"],
    );
    link.href = shuociCopy.aboutProHref;
    link.className = "lianxi-shuoci-pro";
    tellOn(link, shuociCopy.aboutPro);
    note.append(" ", link);
  }

  /** Put a line in the note, with the word to listen to again where given. */
  #say(line: Said, listenTo?: string): void {
    const { note, status } = this.properties.controls;

    tellOn(status, undefined);
    tellOn(note, line);
    note.hidden = false;

    if (listenTo !== undefined) {
      const listen = note.ownerDocument.createElement("button");

      listen.type = "button";
      listen.className = "cb-fuxi-listen";
      tellOn(listen, shuociCopy.listen);
      listen.addEventListener("click", () => {
        this.properties.audio.play(listenTo);
      });
      note.append(" ", listen);
    }
  }

  /** Open the keyboard and put the caret in it. */
  #offerTyping(): void {
    const { typing, field } = this.properties.controls;

    typing.open = true;
    field.focus();
  }

  /** Show the word at the head of the queue, the gate, or the finish. */
  #showHead(): void {
    const { items, progress, finished, gate, next, mark, controls } =
      this.properties;
    const at = this.#queue[0];

    this.#marked = false;

    for (const item of items) {
      item.hidden = true;

      for (const reveal of item.querySelectorAll<HTMLElement>(
        "[data-cb-shuoci-reveal]",
      )) {
        reveal.hidden = true;
      }
    }

    finished.hidden = true;
    next.hidden = true;
    mark.replaceChildren();
    controls.note.hidden = true;
    controls.note.replaceChildren();
    tellOn(controls.status, undefined);
    controls.field.value = "";

    if (at === undefined) {
      this.#showControls(false);
      this.#showFinish();

      return;
    }

    if (gate?.(at) === true) {
      this.#showControls(false);
      progress.hidden = true;
      this.#showMeter();

      return;
    }

    const item = items[at];

    if (item === undefined) {
      return;
    }

    progress.hidden = false;
    progress.textContent = shuociCopy.progress(this.#done() + 1, items.length);
    this.#showMeter();
    item.hidden = false;
    this.#showControls(true);
  }

  /** Show or hide the controls under the words. */
  #showControls(shown: boolean): void {
    const { speak, status, visitor, typing, note } = this.properties.controls;
    const speaking = this.canSpeak();
    const container = typing.parentElement;

    if (container !== null) {
      container.hidden = !shown;
    }

    if (!shown) {
      note.hidden = true;

      return;
    }

    if (speak !== null && speaking) {
      speak.hidden = false;
      speak.disabled = false;
      status?.removeAttribute("hidden");
    }

    if (visitor !== null) {
      visitor.hidden = speaking;
    }

    if (!speaking) {
      typing.open = true;
    }
  }

  /** How many words have been done. */
  #done(): number {
    return this.properties.items.length - this.#queue.length;
  }

  /** Fill the bar under the progress line to the words done. */
  #showMeter(): void {
    const { meter, items } = this.properties;
    const done = this.#done();

    meter?.style.setProperty(
      "--percentage",
      `${String((100 * done) / Math.max(items.length, 1))}%`,
    );
    meter
      ?.querySelector('[role="progressbar"]')
      ?.setAttribute("aria-valuenow", String(done));
  }

  /** The word and its reading, the verdict, and the way on. */
  #showMarked(shown: {
    readonly right: boolean;
    readonly heard?: Said;
    readonly hint?: Said | undefined;
  }): void {
    const { items, next, mark, controls, hear } = this.properties;
    const page = mark.ownerDocument;

    this.#marked = true;

    for (const item of items) {
      if (item.hidden === false) {
        for (const reveal of item.querySelectorAll<HTMLElement>(
          "[data-cb-shuoci-reveal]",
        )) {
          reveal.hidden = false;
        }
      }
    }

    const lines: HTMLElement[] = [];
    const verdict = page.createElement("span");

    verdict.className = "lianxi-shuoci-verdict";
    verdict.textContent = shown.right ? shuociCopy.right : shuociCopy.wrong;
    lines.push(verdict);

    for (const said of [shown.heard, shown.hint]) {
      if (said !== undefined) {
        const line = page.createElement("span");

        line.className = "lianxi-shuoci-heard";
        tellOn(line, said);
        lines.push(line);
      }
    }

    mark.replaceChildren(...lines);
    controls.note.hidden = true;
    tellOn(controls.status, undefined);
    this.#showControls(false);
    hear?.(shown.right);
    next.hidden = false;
    next.focus();
  }

  /** The finish: how many were right the first time, and the hand-over. */
  #showFinish(): void {
    const { finished, progress } = this.properties;

    this.#showMeter();
    progress.hidden = true;
    finished.hidden = false;
    this.#showFinishLine();
  }

  /** The finish line, again once the hand-over is heard of. */
  #showFinishLine(): void {
    const { finished, finishedLine, items, keeper } = this.properties;

    if (finished.hidden !== false) {
      return;
    }

    const count = items.length;

    finishedLine.textContent = joined(
      shuociCopy.finished(count - this.#missed.length, count),
      afterFinish(shuociCopy.handedOver, keeper),
    );
    offerAfterFinish(finishedLine, keeper);
  }

  /** Post a press, and adopt the place the handler answers with. */
  async #post(
    fields: Readonly<Record<string, string>>,
  ): Promise<RecordAnswer | undefined> {
    const { keeper } = this.properties;

    if (keeper === undefined) {
      return undefined;
    }

    const answer = await keeper.press.post({
      ...fields,
      [versionField]: keeper.version,
    });

    this.#adopt(answer);

    return answer;
  }

  /** The handler's answer, taken over the page's own arithmetic. */
  #adopt(answer: RecordAnswer | undefined): void {
    const { keeper } = this.properties;
    const queue = wholeNumbers(answer?.queue);
    const missed = wholeNumbers(answer?.missed);

    if (
      keeper === undefined ||
      answer === undefined ||
      queue === undefined ||
      missed === undefined ||
      answer.version !== keeper.version
    ) {
      return;
    }

    this.#queue = [...queue];
    this.#missed = [...missed];
    keeper.handedOver = answer.handedOver === true;
    keeper.handoverCount = answerHandoverCount(answer);
    this.#showFinishLine();
  }
}
